import React from 'react';
import { formatCurrency } from '../../utils/financialCalculations';
import { useLanguage } from '../../i18n';
import './StatementTables.css';

function BalanceSheetTable({ financials }) {
  const { t } = useLanguage();

  const years = Object.keys(financials).filter(k => k !== 'warnings').map(Number).sort((a, b) => a - b);
  const colSpan = years.length + 1;

  const renderRow = (label, getValue, className = '') => (
    <tr className={className}>
      <td className="row-label">{label}</td>
      {years.map(year => (
        <td key={year} className="number-cell">
          {formatCurrency(getValue(financials[year]))}
        </td>
      ))}
    </tr>
  );

  const renderPercentRow = (label, getValue, className = '') => (
    <tr className={className}>
      <td className="row-label">{label}</td>
      {years.map(year => (
        <td key={year} className="number-cell">
          {(getValue(financials[year]) * 100).toFixed(1)}%
        </td>
      ))}
    </tr>
  );

  const renderSeparator = (label) => (
    <tr className="separator-row">
      <td colSpan={colSpan}><strong>{label}</strong></td>
    </tr>
  );

  const renderCheckRow = () => (
    <tr className="check-row">
      <td className="row-label"><em>{t('bs.checkBalance')}</em></td>
      {years.map(year => {
        const assets = financials[year].assets.totalAssets;
        const liabPlusEquity = financials[year].liabilities.totalLiabilities +
                               financials[year].equity.totalEquity;
        const balanced = Math.abs(assets - liabPlusEquity) < 1;
        return (
          <td key={year} className={`number-cell ${balanced ? 'balanced' : 'unbalanced'}`}>
            {balanced ? '\u2713' : '\u2717 ' + formatCurrency(Math.abs(assets - liabPlusEquity))}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="statement-table-container">
      <h3>{t('bs.title')}</h3>
      <p className="statement-subtitle">{t('bs.subtitle')}</p>

      <div className="table-scroll">
        <table className="statement-table">
          <thead>
            <tr>
              <th className="row-label-header">{t('bs.account')}</th>
              {years.map(year => (
                <th key={year}>{t('bs.year').replace('{year}', year)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Assets */}
            {renderSeparator(t('bs.assets'))}
            {renderRow(t('bs.landValue'), d => d.assets.landValue, 'indent')}
            {renderRow(t('bs.buildingBookValue'), d => d.assets.buildingBookValue, 'indent')}
            {renderRow(t('bs.propertyBookValue'), d => d.assets.propertyBookValue, 'subtotal indent')}
            {renderRow(t('bs.cash'), d => d.assets.cash, 'indent')}
            {renderRow(t('bs.reserveFund'), d => d.assets.reserves, 'indent')}
            {renderRow(t('bs.totalAssets'), d => d.assets.totalAssets, 'total highlight')}

            {/* Liabilities */}
            {renderSeparator(t('bs.liabilities'))}
            {renderRow(t('bs.loanBalance'), d => d.liabilities.loanBalance, 'indent')}
            {renderRow(t('bs.totalLiabilities'), d => d.liabilities.totalLiabilities, 'subtotal')}

            {/* Equity */}
            {renderSeparator(t('bs.equity'))}
            {renderRow(t('bs.initialInvestment'), d => d.equity.initialInvestment, 'indent')}
            {renderRow(t('bs.retainedEarnings'), d => d.equity.retainedEarnings, 'indent')}
            {renderRow(t('bs.totalEquity'), d => d.equity.totalEquity, 'total highlight')}
            {renderRow(t('bs.economicEquity'), d => d.memo?.nav ?? d.equity.totalEquity, 'highlight italic indent')}

            {/* Check Row */}
            {renderCheckRow()}

            {/* Market Value Section */}
            {renderSeparator(t('bs.marketValueSection'))}
            {renderRow(t('bs.landMarketValue'), d => d.memo?.landMarketValue ?? 0, 'indent italic')}
            {renderRow(t('bs.buildingMarketValue'), d => d.memo?.buildingMarketValue ?? 0, 'indent italic')}
            {renderRow(t('bs.estimatedMarketValue'), d => d.memo?.totalMarketValue ?? d.marketValue, 'highlight italic')}
            {renderRow(t('bs.unrealizedGain'), d => d.memo?.unrealizedGain ?? (d.marketValue - d.assets.propertyBookValue), 'italic')}
            {renderPercentRow(t('bs.ltv'), d => d.memo?.ltv ?? 0, 'italic')}
            {renderRow(t('bs.nav'), d => d.memo?.nav ?? 0, 'total highlight italic')}
          </tbody>
        </table>
      </div>

      <div className="statement-notes">
        <h4>{t('bs.notes')}:</h4>
        <ul>
          <li><strong>{t('bs.landValue')}:</strong> {t('bs.landValueNote')}</li>
          <li><strong>{t('bs.buildingBookValue')}:</strong> {t('bs.buildingBookValueNote')}</li>
          <li><strong>{t('bs.estimatedMarketValue')}:</strong> {t('bs.marketValueNote')}</li>
          <li><strong>{t('bs.cash')}:</strong> {t('bs.cashNote')}</li>
          <li><strong>{t('bs.reserveFund')}:</strong> {t('bs.reserveFundNote')}</li>
          <li><strong>{t('bs.retainedEarnings')}:</strong> {t('bs.retainedEarningsNote')}</li>
          <li><strong>{t('bs.ltv')}:</strong> {t('bs.ltvNote')}</li>
          <li><strong>{t('bs.nav')}:</strong> {t('bs.navNote')}</li>
        </ul>
      </div>
    </div>
  );
}

export default BalanceSheetTable;
